import React from 'react';
import { Descriptions, Table, Tag, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;

interface FileSummaryData {
  name: string;
  description?: string;
  filename: string;
  fileType: string;
  transforms: any[];
  hops: any[];
  notes: any[];
  metadata: {
    size: number;
    lastModified: string;
    type: string;
  };
}

interface FileSummaryViewerProps {
  tabId: string;
  data: FileSummaryData;
}

const FileSummaryViewer: React.FC<FileSummaryViewerProps> = ({ tabId, data }) => {
  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleOutlined />
          {data.fileType === 'PIPELINE' ? 'Pipeline Summary' : 'Workflow Summary'}
        </Title>
      </div>

      {/* Basic Information */}
      <Descriptions
        title="Basic Information"
        bordered
        column={1}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Name">{data.name}</Descriptions.Item>
        <Descriptions.Item label="Filename">{data.filename}</Descriptions.Item>
        <Descriptions.Item label="Type">
          <Tag color={data.fileType === 'PIPELINE' ? 'blue' : 'green'}>
            {data.fileType}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {data.description || 'No description'}
        </Descriptions.Item>
        <Descriptions.Item label="File Size">
          {data.metadata.size > 0 ? `${Math.round(data.metadata.size / 1024)} KB` : 'Unknown'}
        </Descriptions.Item>
        <Descriptions.Item label="Last Modified">
          {data.metadata.lastModified ? new Date(data.metadata.lastModified).toLocaleString() : 'Unknown'}
        </Descriptions.Item>
      </Descriptions>

      {/* Statistics */}
      <Descriptions
        title="Statistics"
        bordered
        column={3}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label={data.fileType === 'PIPELINE' ? 'Transforms' : 'Actions'}>
          {data.transforms.length}
        </Descriptions.Item>
        <Descriptions.Item label="Hops">
          {data.hops.length}
        </Descriptions.Item>
        <Descriptions.Item label="Notes">
          {data.notes.length}
        </Descriptions.Item>
      </Descriptions>

      {/* Transforms/Actions Table */}
      {data.transforms.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>
            {data.fileType === 'PIPELINE' ? 'Transforms' : 'Actions'}
          </Title>
          <Table
            dataSource={data.transforms}
            rowKey="name"
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                sorter: (a, b) => a.name.localeCompare(b.name),
              },
              {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                sorter: (a, b) => a.type.localeCompare(b.type),
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
              },
              {
                title: 'Parallel',
                dataIndex: 'parallel',
                key: 'parallel',
                render: (parallel: boolean) => (
                  <Tag color={parallel ? 'green' : 'default'}>
                    {parallel ? 'Yes' : 'No'}
                  </Tag>
                ),
              },
            ]}
            pagination={false}
            size="small"
            scroll={{ y: 200 }}
          />
        </div>
      )}

      {/* Hops Table */}
      {data.hops.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>Hops</Title>
          <Table
            dataSource={data.hops}
            rowKey={(record, index) => `${record.from}-${record.to}-${index}`}
            columns={[
              {
                title: 'From',
                dataIndex: 'from',
                key: 'from',
                sorter: (a, b) => a.from.localeCompare(b.from),
              },
              {
                title: 'To',
                dataIndex: 'to',
                key: 'to',
                sorter: (a, b) => a.to.localeCompare(b.to),
              },
              {
                title: 'Type',
                dataIndex: 'hopType',
                key: 'hopType',
                render: (hopType: string) => (
                  <Tag color={
                    hopType === 'UNCONDITIONAL' ? 'blue' :
                    hopType === 'TRUE' ? 'green' : 'orange'
                  }>
                    {hopType}
                  </Tag>
                ),
              },
              {
                title: 'Enabled',
                dataIndex: 'enabled',
                key: 'enabled',
                render: (enabled: boolean) => (
                  <Tag color={enabled ? 'green' : 'red'}>
                    {enabled ? 'Yes' : 'No'}
                  </Tag>
                ),
              },
            ]}
            pagination={false}
            size="small"
            scroll={{ y: 200 }}
          />
        </div>
      )}
    </div>
  );
};

export default FileSummaryViewer;
